import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Progress } from "./ui/progress";
import { generateAdvice } from "../utils/guiltAlgorithm";
import ShareStatsCard from "./ShareStatsCard";

const EnhancedGuiltGauge = ({ purchase, purchaseHistory, goToInput, goToDashboard, theme }) => {
  const [showAdvice, setShowAdvice] = useState(false);
  const [advice, setAdvice] = useState(null);
  
  useEffect(() => {
    // Generate advice based on purchase and guilt score
    setAdvice(generateAdvice(purchase, purchase.guiltScore));
  }, [purchase]);
  
  // Get guilt level and corresponding styles
  const getGuiltLevel = (score) => {
    if (score < 30) return { level: "low", color: "bg-green-500", text: "text-green-600" };
    if (score < 70) return { level: "medium", color: "bg-yellow-500", text: "text-yellow-600" };
    return { level: "high", color: "bg-red-500", text: "text-red-600" };
  };
  
  const guiltInfo = getGuiltLevel(purchase.guiltScore);
  
  // Get label for guilt score
  const getGuiltLabel = (score) => {
    if (score < 20) return "Guilt-Free";
    if (score < 40) return "Slight Regret";
    if (score < 60) return "Mixed Feelings";
    if (score < 80) return "Buyer's Remorse";
    return "Major Guilt";
  };
  
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600 text-white",
    pink: "bg-pink-500 hover:bg-pink-600 text-white",
    lavender: "bg-purple-500 hover:bg-purple-600 text-white"
  };
  
  // Calculate time-based savings message
  const getSavingsMessage = () => {
    const amount = parseFloat(purchase.amount) || 0;
    // Calculate how much this would be worth if invested
    const yearlyReturn = 0.07; // 7% average stock market return
    const fiveYearReturn = amount * Math.pow(1 + yearlyReturn, 5);
    return `$${amount.toFixed(2)} invested today could be worth $${fiveYearReturn.toFixed(2)} in 5 years.`;
  };

  const getPersonalityType = () => {
    // If we don't have enough purchase history, return a default message for a first-time purchase
    if (!purchaseHistory || purchaseHistory.length < 5) {
      if (purchase.guiltScore < 30) return "Smart Shopper";
      if (purchase.guiltScore > 70) return "Impulse Buyer";
      return "Casual Consumer";
    }
    
    // If we have history, use more detailed personality analysis
    const moodCounts = purchaseHistory.reduce((acc, p) => {
      acc[p.mood] = (acc[p.mood] || 0) + 1;
      return acc;
    }, {});
    
    const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0][0];
    const avgGuilt = purchaseHistory.reduce((sum, p) => sum + p.guiltScore, 0) / purchaseHistory.length;
    
    if (topMood === "bored" && avgGuilt > 70) return "Emotional Magpie";
    if (topMood === "celebrating" && avgGuilt < 40) return "Treat Yo'self Champion";
    if (avgGuilt > 60) return "Luxury Justifier";
    if (avgGuilt < 30) return "Mindful Spender";
    if (topMood === "fomo") return "FOMO Shopper";
    if (topMood === "drunk") return "Wine & Prime Shopper";
    
    return "Discount Daredevil";
  };
  
  // Define progress bar color based on guilt score
  const progressBarColor = purchase.guiltScore < 30
    ? "bg-green-500"
    : purchase.guiltScore < 70
    ? "bg-yellow-500"
    : "bg-red-500";
  
  return (
    <motion.div
      key="enhanced-guilt-gauge"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-3xl shadow-xl p-6 mb-8"
    >
      <h2 className="text-2xl font-bold text-center mb-4">Your Guilt Gauge</h2>

      <div className="text-center text-gray-600 mb-6">
        <div className="font-medium text-lg">{purchase.item}</div>
        <div className="text-sm">
          ${parseFloat(purchase.amount).toFixed(2)} • {new Date(purchase.date).toLocaleDateString()}
        </div>
      </div>

      {/* Improved Gauge Visualization */}
      <div className="relative mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1 px-2">
          <span>0</span>
          <span>Guilt Score</span>
          <span>100</span>
        </div>
        
        <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${progressBarColor} transition-all`}
            style={{ width: `${purchase.guiltScore}%` }}
          ></div>
        </div>
        
        <div className="flex justify-between px-2 mt-1">
          <span className="text-green-600 text-xs">Guilt Free</span>
          <span className={`font-semibold ${guiltInfo.text}`}>
            {purchase.guiltScore}% - {getGuiltLabel(purchase.guiltScore)}
          </span>
          <span className="text-red-600 text-xs">Major Regret</span>
        </div>
      </div>
      
      {/* Mood and Context Info */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <div className="text-sm text-gray-500">Mood</div>
          <div className="text-xl mt-1">
            {purchase.mood === "sad" ? "😢" : 
             purchase.mood === "happy" ? "😊" : 
             purchase.mood === "bored" ? "😑" :
             purchase.mood === "drunk" ? "🍸" :
             purchase.mood === "celebrating" ? "🎉" :
             purchase.mood === "cute" ? "💅" :
             purchase.mood === "fomo" ? "👀" : "🛒"}
          </div>
          <div className="text-sm font-medium capitalize">{purchase.mood}</div>
        </div>
        
        <div className="bg-gray-50 rounded-xl p-3 text-center">
          <div className="text-sm text-gray-500">Usage</div>
          <div className="text-xl mt-1">
            {purchase.usageFrequency === "daily" ? "📅" : 
             purchase.usageFrequency === "weekly" ? "🗓️" : 
             purchase.usageFrequency === "occasionally" ? "🔄" :
             purchase.usageFrequency === "rarely" ? "⏰" : "❓"}
          </div>
          <div className="text-sm font-medium capitalize">{purchase.usageFrequency}</div>
        </div>
      </div>
      
      {/* Advice Toggle Button */}
      <motion.button
        onClick={() => setShowAdvice(!showAdvice)}
        className={`w-full py-3 rounded-xl ${buttonStyles[theme]} font-semibold mb-4 flex justify-center items-center`}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {showAdvice ? "Hide Advice" : "Get Shopping Advice"}
        <span className="ml-2">{showAdvice ? "👇" : "💡"}</span>
      </motion.button>
      
      {/* Shopping Advice Section */}
      {showAdvice && advice && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-4"
        >
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center mb-2">
              <span className="text-2xl mr-2">{advice.emoji}</span>
              <h3 className="font-bold">{advice.title}</h3>
            </div>
            
            <p className="text-gray-600 mb-3">{advice.message}</p>
            
            <div className="space-y-2">
              {advice.tips.map((tip, index) => (
                <div key={index} className="flex items-start">
                  <div className="text-xs bg-white rounded-full h-5 w-5 flex items-center justify-center mr-2 mt-0.5 border">
                    {index + 1}
                  </div>
                  <div className="text-sm">{tip}</div>
                </div>
              ))}
            </div>
            
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 italic">
              {getSavingsMessage()}
            </div>

            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500 italic">
              {purchase.guiltScore > 50 ? (
                <div className="flex items-center justify-between">
                  <span>Looking for mindful alternatives to reduce shopping guilt?</span>
                  <motion.button
                    onClick={() => window.location.href = '/shop'}
                    className="text-blue-500 hover:text-blue-700 underline"
                    whileHover={{ scale: 1.05 }}
                  >
                    Explore
                  </motion.button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span>Discover products that align with your mindful spending habits.</span>
                  <motion.button
                    onClick={() => window.location.href = '/shop'}
                    className="text-blue-500 hover:text-blue-700 underline"
                    whileHover={{ scale: 1.05 }}
                  >
                    Browse
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-4">
        <motion.button
          onClick={goToInput}
          className="py-3 rounded-xl border border-gray-300 font-semibold"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Log Another
        </motion.button>
        <motion.button
          onClick={goToDashboard}
          className="py-3 rounded-xl border border-gray-300 font-semibold"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          View Dashboard
        </motion.button>
      </div>

      <ShareStatsCard 
        purchaseData={purchase} 
        theme={theme} 
        userPersonality={getPersonalityType()}
        cardType="purchase" // Explicitly set to "purchase" for individual purchase stats
      />
    </motion.div>
  );
};

export default EnhancedGuiltGauge;
